import type { JSX } from 'react';

import type { FieldPath, FieldValues, UseFormRegisterReturn } from 'react-hook-form';

import NewSelect from '@/components/ui/NewSelect';
import appIcons from '@/constants/appIcons';
import useI18n from '@/hooks/useI18n';
import type { ValidationError } from '@/types/ValidationError';

import useFetchCountries from '../hooks/useFetchCountries';
import type { Country } from '../types/Country';

type NewCountriesDropdownProps<T extends FieldValues> = {
    country: Country | null;
    setCountry: (country: Country | null) => void;
    label?: string;
    hasPrefixIcon?: boolean;
    validationError?: ValidationError;
    errorMessage?: string;
    className?: string;
    register?: UseFormRegisterReturn<FieldPath<T>>;
};

export default function NewCountriesDropdown<T extends FieldValues>({
    country,
    setCountry,
    label,
    hasPrefixIcon = true,
    errorMessage,
    className,
    register,
}: NewCountriesDropdownProps<T>): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();

    const { countries, isLoading } = useFetchCountries({ perPage: 1000, sortBy: 'name', orderBy: 'asc' });

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <NewSelect
            {...register}
            placeholder={t('Common.Country_Placeholder')}
            prefixIcon={hasPrefixIcon ? appIcons.outline.country : undefined}
            label={label}
            options={countries}
            value={country}
            optionIdKey="id"
            optionTitleKey="name"
            className={className}
            errorMessage={errorMessage}
            isLoading={isLoading}
            isClearable={false}
            isRequired
            onChange={setCountry}
        />
    );
}
